import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BizEntityCatalog,
  type ColumnConfig,
  useBizEntityCatalog,
  type UseSearchMetaHookResult,
} from '@saviynt/biz-entity-catalog';
import { AlertBanner, Chip, Tooltip, Typography } from '@saviynt/design-system';

import { ConfirmOption } from '../../../shared/components/ConfirmOption';
import { useInvertedLocalize } from '../../../shared/utils/localization';
import {
  invalidateClientGetById,
  useAllowedRoles,
  useClientCreate,
  useClientDelete,
  useClientDisable,
  useClientEnable,
  useClientGetById,
  useClientSearch,
  useClientUpdate,
  useDeleteWithCaptcha,
} from '../hooks';
import { type Client, type CreateClientResult } from '../types';

import { ActionsCell } from './ActionsCell';
import { ClaimsTooltip } from './ClaimsTooltip';
import { ClientForm } from './ClientForm';
import { CopyButton } from './CopyButton';
import { CreateClientButton } from './CreateClientButton';
import { RolesCell } from './RolesCell';
import { SuccessDialog } from './SuccessDialog';

import styles from './ClientCredentialsListPage.module.css';

/** Static filter metadata for the enabled/disabled status filter */
const STATUS_FILTER_META: UseSearchMetaHookResult = {
  filters: [
    {
      field: 'enabled',
      label: 'Status',
      values: [
        { label: 'Enabled', value: 'true' },
        { label: 'Disabled', value: 'false' },
      ],
    },
  ],
  isLoading: false,
  error: null,
};

/** Hook returning static status filter metadata for BizEntityCatalog */
function useClientSearchFilters(): UseSearchMetaHookResult {
  return STATUS_FILTER_META;
}

/**
 * Bridge component that syncs BizEntityCatalog context's refetch
 * to a callback owned by the parent. Renders nothing.
 */
function ContextBridge({
  onRefetch,
}: {
  onRefetch: React.MutableRefObject<() => void>;
}): null {
  const { refetch } = useBizEntityCatalog();

  // Keep the parent's ref in sync without triggering no-param-reassign
  // (useEffect ensures we don't assign during render of a different component)
  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    onRefetch.current = refetch;
  }, [refetch, onRefetch]);

  return null;
}

/**
 * Main list page for client credentials management.
 * Wires BizEntityCatalog with all CRUD hooks, column config, and custom modals.
 */
export function ClientCredentialsListPage(): JSX.Element {
  const l = useInvertedLocalize();
  const { roles: allowedRoles, error: rolesError } = useAllowedRoles();

  // Allow user to dismiss the roles-error banner
  const [rolesErrorDismissed, setRolesErrorDismissed] = useState(false);

  // Ref to capture refetch from BizEntityCatalog context via ContextBridge
  const refetchRef = useRef<() => void>(() => {});

  // Custom delete flow (captcha confirmation → API call → success toast → refetch)
  const {
    requestDelete,
    confirmRequest: deleteConfirmRequest,
    clearRequest: clearDeleteRequest,
    successMessage,
    clearSuccessMessage,
    errorMessage: deleteErrorMessage,
    clearErrorMessage: clearDeleteErrorMessage,
  } = useDeleteWithCaptcha(() => refetchRef.current(), l);

  // Track create response data for the success dialog
  const [successData, setSuccessData] = useState<{
    clientId: string;
    audience: string;
    roles: string[];
    customClaims?: Record<string, string>;
  } | null>(null);

  const columns: ColumnConfig<Client>[] = useMemo(
    () => [
      {
        key: 'clientName',
        label: l('Client Name'),
        width: 266,
        isPinned: true,
        sortable: false,
        render: (item: Client) => (
          <Typography kind='body2-bold'>{item.clientName}</Typography>
        ),
      },
      {
        key: 'clientId',
        label: l('Client ID'),
        width: 182,
        sortable: false,
        render: (item: Client) => (
          <div className={styles.ClientIdCell}>
            <Typography kind='body2'>{item.clientId}</Typography>
            <CopyButton text={item.clientId} />
          </div>
        ),
      },
      {
        key: 'description',
        label: l('Description'),
        width: 260,
        sortable: false,
        render: (item: Client) => {
          const description = item.description ?? '';

          return (
            <Tooltip text={description}>
              <Typography kind='body2'>
                {description.length > 60
                  ? // eslint-disable-next-line formatjs/no-literal-string-in-jsx -- truncation ellipsis
                    `${description.slice(0, 60)}...`
                  : description}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        key: 'roles',
        label: l('Roles'),
        sortable: false,
        render: (item: Client) => <RolesCell roles={item.roles} />,
      },
      {
        key: 'customClaims',
        label: l('Custom Claims'),
        sortable: false,
        render: (item: Client) => <ClaimsTooltip claims={item.customClaims} />,
      },
      {
        key: 'enabled',
        label: l('Status'),
        sortable: false,
        render: (item: Client) => (
          <Chip
            label={item.enabled ? l('Enabled') : l('Disabled')}
            color={item.enabled ? 'success' : 'critical'}
            kind='subtleFilled'
            size='small'
          />
        ),
      },
      {
        key: 'actions',
        label: '',
        width: 126,
        sortable: false,
        render: (item: Client) => (
          <ActionsCell item={item} onDelete={requestDelete} />
        ),
      },
    ],
    [l, requestDelete]
  );

  const handleCreateSuccess = (data: Client) => {
    const result = data as CreateClientResult;

    setSuccessData({
      clientId: result.clientId,
      audience: result.audience,
      roles: result.roles,
      customClaims: result.customClaims,
    });
  };

  return (
    <>
      <BizEntityCatalog<Client>
        useSearchHook={useClientSearch}
        useSearchFiltersHook={useClientSearchFilters}
        useCreateHook={useClientCreate}
        useUpdateHook={useClientUpdate}
        useDeleteHook={useClientDelete}
        useDisableHook={useClientDisable}
        useEnableHook={useClientEnable}
        useGetByIdHook={useClientGetById}
        searchConfig={{
          pageSize: 20,
        }}
        renderForm={(props) => (
          <ClientForm
            mode={props.mode}
            data={props.data}
            onSubmit={props.onSubmit}
            onCancel={props.onCancel}
            isSubmitting={props.isSubmitting}
            validationErrors={props.validationErrors}
            allowedRoles={allowedRoles}
            getFormData={props.getFormData}
            formRef={props.formRef}
          />
        )}
        listConfig={{
          viewType: 'table',
          columns,
          filterFields: [{ field: 'enabled', label: 'Status' }],
          searchPlaceholder: l('Search all providers'),
          emptyStateMessage: l('No client credentials found.'),
        }}
        modalConfig={{
          createTitle: l('Create Client'),
          editTitle: l('Edit Client'),
        }}
        features={{
          enableCreate: true,
          enableEdit: true,
          enableDelete: false, // Custom delete modal (captcha)
          enableDisable: true,
          enableSearch: true,
          enableSort: false,
          enableFilters: true,
          enableInfiniteScroll: true,
          enableModal: true,
          showCreateButton: false, // Custom create button in header
        }}
        disableConfirmConfig={{
          message: l(
            'Are you sure you want to disable this client? Existing integrations may fail.'
          ),
        }}
        onCreateSuccess={handleCreateSuccess}
        onUpdateSuccess={invalidateClientGetById}>
        <ContextBridge onRefetch={refetchRef} />
        <BizEntityCatalog.Header title={l('OAuth Client Credentials')}>
          <CreateClientButton hasRolesError={rolesError} />
        </BizEntityCatalog.Header>
        <BizEntityCatalog.ListView>
          {/* eslint-disable @typescript-eslint/no-unsafe-assignment -- DS AlertBanner onCancel type uses `any` */}
          <AlertBanner
            className={styles.Alert}
            colorTheme='Warning'
            kind='high'
            description={l(
              'Unable to load roles. Create is disabled until roles are available.'
            )}
            isVisible={rolesError && !rolesErrorDismissed}
            onCancel={() => setRolesErrorDismissed(true)}
          />
          <AlertBanner
            className={styles.Alert}
            colorTheme='Success'
            kind='high'
            description={successMessage ?? ''}
            isVisible={!!successMessage}
            shouldAutoDismiss
            autoDismissDelay={5000}
            onCancel={clearSuccessMessage}
          />
          <AlertBanner
            className={styles.Alert}
            colorTheme='Critical'
            kind='high'
            description={deleteErrorMessage ?? ''}
            isVisible={!!deleteErrorMessage}
            shouldAutoDismiss
            autoDismissDelay={5000}
            onCancel={clearDeleteErrorMessage}
          />
        </BizEntityCatalog.ListView>
      </BizEntityCatalog>

      {/* Type-to-confirm delete dialog (shared ConfirmOption pattern) */}
      <ConfirmOption
        confirmRequest={deleteConfirmRequest}
        clearRequest={clearDeleteRequest}
      />

      {/* Post-create success dialog */}
      <SuccessDialog
        isOpen={successData !== null}
        onClose={() => setSuccessData(null)}
        clientId={successData?.clientId ?? ''}
        audience={successData?.audience ?? ''}
        roles={successData?.roles ?? []}
        customClaims={successData?.customClaims ?? {}}
      />
    </>
  );
}
